import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ProjectDropFile } from '../components/BatchRenameModal';
import { uploadDocuments } from '../utils/uploadDocuments';

const ACCEPTED_UPLOAD_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'eml', 'msg', 'txt'];

const hasAcceptedFile = (dt: DataTransfer): boolean => {
  if (!dt || !dt.types) return false;
  return Array.from(dt.types).includes('Files');
};

interface DropTargetProject {
  id: string;
  customerId: string;
  title: string;
}

interface UseProjectFileDropOptions {
  project: DropTargetProject;
  onDocumentsUploaded?: (count: number) => void;
  /**
   * When supplied, a drop bypasses this hook's own rename modal and instead
   * calls this with the dropped files and an upload function bound to this
   * project — the caller owns a single shared modal (e.g. one per list,
   * rather than one per row/tile). When omitted, the hook manages its own
   * modal via `renameModalProps` (the tile-drop / ProjectCard behavior).
   */
  onFilesDropped?: (files: File[], performUpload: (rows: ProjectDropFile[]) => Promise<void>) => void;
}

export interface DuplicateUploadNotice {
  message: string;
  fileName: string;
  uploadedDocumentId: string;
  duplicateOfDocumentId: string;
}

export interface UseProjectFileDropResult {
  dragProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  isDragging: boolean;
  uploading: { done: number; total: number } | null;
  uploadError: string | null;
  duplicateNotice: DuplicateUploadNotice | null;
  dismissDuplicateNotice: () => void;
  performUpload: (rows: ProjectDropFile[]) => Promise<void>;
  /** null when `onFilesDropped` was supplied — the caller owns the modal. */
  renameModalProps: {
    isOpen: boolean;
    files: File[];
    projectTitle: string;
    onCancel: () => void;
    onConfirm: (rows: ProjectDropFile[]) => void;
  } | null;
}

export function useProjectFileDrop({ project, onDocumentsUploaded, onFilesDropped }: UseProjectFileDropOptions): UseProjectFileDropResult {
  const { token } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [duplicateNotice, setDuplicateNotice] = useState<DuplicateUploadNotice | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [renameOpen, setRenameOpen] = useState(false);

  const onDragEnter = (e: React.DragEvent) => {
    if (!hasAcceptedFile(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragOver = (e: React.DragEvent) => {
    if (!hasAcceptedFile(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (e: React.DragEvent) => {
    // Ignore leave events from inner children
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const performUpload = async (rows: ProjectDropFile[]) => {
    if (!token) return;
    setRenameOpen(false);
    setUploading({ done: 0, total: rows.length });
    setUploadError(null);

    const renamedFiles = rows.map(({ file, newName }) => new File([file], newName, { type: file.type }));
    const { uploaded, failures } = await uploadDocuments(
      renamedFiles,
      { customerId: project.customerId, projectId: project.id, token },
      (done, total) => setUploading({ done, total })
    );

    const duplicate = uploaded.filter((u) => u.duplicateOfDocumentId).pop();
    if (duplicate) {
      // Persistent (until dismissed) — the user picks an action rather
      // than the notice disappearing before they've read it.
      setDuplicateNotice({
        message: `"${duplicate.name}" looks identical to a document already in this project.`,
        fileName: duplicate.name,
        uploadedDocumentId: duplicate.id,
        duplicateOfDocumentId: duplicate.duplicateOfDocumentId!,
      });
    }

    setUploading(null);
    setPendingFiles([]);
    if (failures > 0) {
      setUploadError(`${failures} of ${rows.length} files failed to upload`);
      window.setTimeout(() => setUploadError(null), 4000);
    }
    if (failures < rows.length) {
      onDocumentsUploaded?.(rows.length - failures);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    if (!hasAcceptedFile(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files || []).filter((f) => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return ext && ACCEPTED_UPLOAD_EXTS.includes(ext);
    });
    if (files.length === 0) {
      setUploadError('No supported files in drop');
      window.setTimeout(() => setUploadError(null), 3000);
      return;
    }
    if (!token) return;

    if (onFilesDropped) {
      onFilesDropped(files, performUpload);
      return;
    }

    // Show the rename modal first instead of uploading immediately; matches
    // the workflow-modal upload flow so users get a chance to set a name.
    setPendingFiles(files);
    setRenameOpen(true);
  };

  const cancelRename = () => {
    setRenameOpen(false);
    setPendingFiles([]);
  };

  return {
    dragProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
    isDragging,
    uploading,
    uploadError,
    duplicateNotice,
    dismissDuplicateNotice: () => setDuplicateNotice(null),
    performUpload,
    renameModalProps: onFilesDropped
      ? null
      : {
          isOpen: renameOpen,
          files: pendingFiles,
          projectTitle: project.title,
          onCancel: cancelRename,
          onConfirm: performUpload,
        },
  };
}
